'use client';

import { useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { X, Globe } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { isCountrySupportedOnLocale, getRecommendedLocale, LOCALE_SHIPPING_COUNTRIES } from '@/lib/pricing';
import { useCartStore } from '@/store/cart-store';
import { productPath } from '@/lib/localized-paths';
import { CountryFlag } from '@/components/country-flag';

const COUNTRY_NAMES: Record<string, string> = {
  'NL': 'Nederland',
  'BE': 'België',
  'DE': 'Duitsland',
  'FR': 'Frankrijk',
  'DK': 'Denemarken',
  'ES': 'Spanje',
  'IT': 'Italië',
  'LU': 'Luxemburg',
  'PL': 'Polen',
  'AT': 'Oostenrijk',
  'CZ': 'Tsjechië',
  'EE': 'Estland',
  'HR': 'Kroatië',
  'HU': 'Hongarije',
  'LT': 'Litouwen',
  'LV': 'Letland',
  'SE': 'Zweden',
  'SI': 'Slovenië',
  'SK': 'Slowakije',
};

const COUNTRY_NAMES_BY_LOCALE: Record<string, Record<string, string>> = {
  nl: COUNTRY_NAMES,
  de: { NL: 'Niederlande', BE: 'Belgien', DE: 'Deutschland', FR: 'Frankreich', DK: 'Dänemark', ES: 'Spanien', IT: 'Italien', LU: 'Luxemburg', PL: 'Polen', AT: 'Österreich', CZ: 'Tschechien', EE: 'Estland', HR: 'Kroatien', HU: 'Ungarn', LT: 'Litauen', LV: 'Lettland', SE: 'Schweden', SI: 'Slowenien', SK: 'Slowakei' },
  en: { NL: 'the Netherlands', BE: 'Belgium', DE: 'Germany', FR: 'France', DK: 'Denmark', ES: 'Spain', IT: 'Italy', LU: 'Luxembourg', PL: 'Poland', AT: 'Austria', CZ: 'Czechia', EE: 'Estonia', HR: 'Croatia', HU: 'Hungary', LT: 'Lithuania', LV: 'Latvia', SE: 'Sweden', SI: 'Slovenia', SK: 'Slovakia' },
  fr: { NL: 'Pays-Bas', BE: 'Belgique', DE: 'Allemagne', FR: 'France', DK: 'Danemark', ES: 'Espagne', IT: 'Italie', LU: 'Luxembourg', PL: 'Pologne', AT: 'Autriche', CZ: 'Tchéquie', EE: 'Estonie', HR: 'Croatie', HU: 'Hongrie', LT: 'Lituanie', LV: 'Lettonie', SE: 'Suède', SI: 'Slovénie', SK: 'Slovaquie' },
};

const LOCALE_NAMES: Record<string, Record<string, string>> = {
  nl: { nl: 'Nederlandse', de: 'Duitse', fr: 'Franse', en: 'Engelse' },
  de: { nl: 'Niederländisch', de: 'Deutsch', fr: 'Französisch', en: 'Englisch' },
  en: { nl: 'Dutch', de: 'German', fr: 'French', en: 'English' },
  fr: { nl: 'néerlandaise', de: 'allemande', fr: 'française', en: 'anglaise' },
};

const BANNER_COPY: Record<string, {
  warning: string;
  visiting: string;
  shipping: string;
  shippingShort: string;
  switchQuestion: string;
  goTo: string;
  version: string;
  shippingCountryLabel: string;
  close: string;
}> = {
  nl: {
    warning: 'Let op:',
    visiting: 'U bezoekt de {locale} versie.',
    shipping: 'Verzending is beschikbaar naar {countries}.',
    shippingShort: 'Verzending naar {count} EU-landen.',
    switchQuestion: 'Wilt u naar de {locale} versie?',
    goTo: 'Ga naar {locale} versie',
    version: '',
    shippingCountryLabel: 'Verzendland',
    close: 'Sluiten',
  },
  de: {
    warning: 'Hinweis:',
    visiting: 'Sie besuchen Printen.eu auf {locale}.',
    shipping: 'Versand ist verfügbar nach {countries}.',
    shippingShort: 'Versand in {count} EU-Länder.',
    switchQuestion: 'Möchten Sie zu {locale} wechseln?',
    goTo: 'Zu {locale} wechseln',
    version: '',
    shippingCountryLabel: 'Lieferland',
    close: 'Schließen',
  },
  en: {
    warning: 'Note:',
    visiting: 'You are visiting the {locale} version.',
    shipping: 'Shipping is available to {countries}.',
    shippingShort: 'Shipping to {count} EU countries.',
    switchQuestion: 'Would you like to switch to the {locale} version?',
    goTo: 'Go to {locale} version',
    version: '',
    shippingCountryLabel: 'Shipping country',
    close: 'Close',
  },
  fr: {
    warning: 'Attention :',
    visiting: 'Vous consultez la version {locale}.',
    shipping: 'La livraison est disponible vers {countries}.',
    shippingShort: 'Livraison dans {count} pays de l’UE.',
    switchQuestion: 'Voulez-vous passer à la version {locale} ?',
    goTo: 'Aller à la version {locale}',
    version: '',
    shippingCountryLabel: 'Pays de livraison',
    close: 'Fermer',
  },
};

export function CountryBanner({ currentLocale }: { currentLocale: string }) {
  const [visitorCountry, setVisitorCountry] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const { selectedCountryCode, setCountry } = useCartStore();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    // Check if already dismissed in this session
    const wasDismissed = sessionStorage.getItem('country-banner-dismissed');
    if (wasDismissed) {
      setDismissed(true);
      return;
    }

    // Detect visitor country through our own route so the browser never calls a third-party IP API.
    fetch('/api/detect-location')
      .then(res => res.json())
      .then(data => {
        const country = data.country;
        setVisitorCountry(country);
      })
      .catch(() => {
        // Silently fail - banner won't show
      });
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('country-banner-dismissed', 'true');
  };

  const handleSwitchLocale = async () => {
    if (!visitorCountry) return;

    const recommendedLocale = getRecommendedLocale(visitorCountry);
    const currentPath = pathname.replace(/^\/(nl|de|en|fr)/, '') || '/';
    const productMatch = currentPath.match(/^\/(producten|produkte|products|produits)\/([^/?#]+)/);

    if (productMatch) {
      try {
        const currentSlug = decodeURIComponent(productMatch[2]);
        const response = await fetch(`/api/products/localized-slug?slug=${encodeURIComponent(currentSlug)}&locale=${encodeURIComponent(recommendedLocale)}`);
        if (response.ok) {
          const data = await response.json();
          if (data?.slug) {
            router.push(`/${recommendedLocale}${productPath(recommendedLocale, data.slug)}`);
            return;
          }
        }
      } catch {
        // Fall through to route-only switch.
      }
    }

    const newPath = pathname.replace(`/${currentLocale}`, `/${recommendedLocale}`);
    router.push(newPath);
  };

  // Don't show banner if:
  // - Dismissed
  // - No visitor country detected
  // - Visitor country is supported on current locale
  if (dismissed || !visitorCountry || isCountrySupportedOnLocale(visitorCountry, currentLocale)) {
    return null;
  }

  const supportedOnLocale = isCountrySupportedOnLocale(visitorCountry, currentLocale);

  // Only show if visitor is from a different country that's not supported
  if (supportedOnLocale) {
    return null;
  }

  const copy = BANNER_COPY[currentLocale] || BANNER_COPY.nl;
  const localeNames = LOCALE_NAMES[currentLocale] || LOCALE_NAMES.nl;
  const countryNames = COUNTRY_NAMES_BY_LOCALE[currentLocale] || COUNTRY_NAMES_BY_LOCALE.nl;
  const localeName = localeNames[currentLocale] || currentLocale;
  const recommendedLocale = getRecommendedLocale(visitorCountry);
  const supportedCountries = LOCALE_SHIPPING_COUNTRIES[currentLocale] || [];
  const selectedCountry = selectedCountryCode && supportedCountries.includes(selectedCountryCode)
    ? selectedCountryCode
    : supportedCountries[0] || visitorCountry;
  const supportedCountryNames = supportedCountries
    .map((country) => countryNames[country] || country)
    .join(', ');

  // Eén smalle regel: de volledige landenlijst nam op mobiel het hele scherm
  // in. De lijst staat nog in de title en in de landkeuze.
  return (
    <div className="border-b border-blue-100 bg-blue-50 text-blue-900">
      <div className="container mx-auto flex items-center gap-2 px-4 py-1.5 text-xs sm:text-sm">
        <Globe className="h-4 w-4 flex-shrink-0 text-blue-600" aria-hidden="true" />
        <p className="min-w-0 flex-1 truncate" title={copy.shipping.replace('{countries}', supportedCountryNames)}>
          {copy.shippingShort.replace('{count}', String(supportedCountries.length))}
          {recommendedLocale !== currentLocale && (
            <>
              {' '}
              <button type="button" onClick={handleSwitchLocale} className="font-semibold underline underline-offset-2">
                {copy.goTo.replace('{locale}', localeNames[recommendedLocale] || recommendedLocale)}
              </button>
            </>
          )}
        </p>
        <Select value={selectedCountry} onValueChange={setCountry}>
          <SelectTrigger
            aria-label={copy.shippingCountryLabel}
            className="h-7 w-auto max-w-[9rem] gap-1 border-blue-200 bg-white px-2 text-xs text-blue-900"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {supportedCountries.map((country) => (
              <SelectItem key={country} value={country}>
                <CountryFlag code={country} />
                {countryNames[country] || country}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={handleDismiss}
          className="flex-shrink-0 rounded p-1 text-blue-700 transition hover:bg-blue-100"
          aria-label={copy.close}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
